import React, { useState, useEffect } from 'react';
import { ArrowDownCircle, ArrowUpCircle, ArrowRightCircle } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOVEMENT_CONFIG = {
  IN: { label: 'Stock In', color: 'bg-green-100 text-green-800', icon: ArrowDownCircle },
  OUT: { label: 'Stock Out', color: 'bg-red-100 text-red-800', icon: ArrowUpCircle },
  TRANSFER: { label: 'Transfer', color: 'bg-blue-100 text-blue-800', icon: ArrowRightCircle },
  VIRTUAL_IN: { label: 'Virtual In', color: 'bg-purple-100 text-purple-800', icon: ArrowDownCircle },
  VIRTUAL_OUT: { label: 'Virtual Out', color: 'bg-orange-100 text-orange-800', icon: ArrowUpCircle },
};

const StockMovements = () => {
  const { toast } = useToast();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchMovements();
  }, [filter]);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter) params.append('movement_type', filter);
      params.append('limit', '100');

      const response = await fetch(`${API_URL}/api/inventory/movements?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch movements');
      const data = await response.json();
      setMovements(data);
    } catch (error) {
      console.error('Error fetching movements:', error);
      toast({
        title: 'Error',
        description: 'Failed to load stock movements',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <BackButton />

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Stock Movements</h2>
            <p className="text-sm text-[#6C757D]">History of all stock transactions</p>
          </div>
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            >
              <option value="">All Movements</option>
              <option value="IN">Stock In</option>
              <option value="OUT">Stock Out</option>
              <option value="TRANSFER">Transfers</option>
              <option value="VIRTUAL_IN">Virtual In</option>
              <option value="VIRTUAL_OUT">Virtual Out</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading movements...</p>
          </CardContent>
        </Card>
      ) : movements.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">No stock movements found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {movements.map((movement) => {
            const config = MOVEMENT_CONFIG[movement.movement_type] || MOVEMENT_CONFIG.IN;
            const Icon = config.icon;
            
            return (
              <Card key={movement.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`p-3 rounded-lg ${config.color} bg-opacity-20`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-[#2B2D42]">
                            {movement.product_name || 'Unknown Product'}
                          </h3>
                          <Badge className={config.color}>{config.label}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#6C757D]">
                          <div>
                            <span className="font-medium">Quantity:</span> {movement.quantity}
                          </div>
                          <div>
                            <span className="font-medium">Source:</span> {movement.source}
                          </div>
                          <div>
                            <span className="font-medium">User:</span> {movement.user_email || 'System'}
                          </div>
                          <div>
                            <span className="font-medium">Time:</span> {formatDate(movement.timestamp)}
                          </div>
                          {movement.reference_id && (
                            <div className="col-span-2">
                              <span className="font-medium">Reference:</span> {movement.reference_id}
                            </div>
                          )}
                          {movement.notes && (
                            <div className="col-span-2">
                              <span className="font-medium">Notes:</span> {movement.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StockMovements;